import React, { useState, useEffect, useContext, useRef } from "react";
import axios from "axios";

import { UserContext } from "../../context/UserContext";
import HeroPanel from "./HeroPanel";
import HeroStatsBar from "./HeroStatsBar";
import MovementPanel from "./MovementPanel";
import DeathPanel from "./DeathPanel";
import CombatChoicePanel from "./CombatChoicePanel";
import CombatPanel from "./CombatPanel";
import CombatResultPanel from "./CombatResultPanel";
import TrapChoicePanel from "./TrapChoicePanel";
import GouffreFallPanel from "./GouffreFallPanel";
import BugReportForm from "./BugReportForm";
import Minimap from "./Minimap";
import EnemyChoicePanel from "./EnemyChoicePanel";
import { KeyPanel, ChestPanel, ShopPanel } from "./InteractionPanels";
import InventoryPanel from "./InventoryPanel";
import { inventoryImage } from "./images";
import DungeonGrid from "./DungeonGrid";
import ActionOverlay from "./ActionOverlay";
import { VictoryScreen, GameOverScreen, DefeatScreen } from "./EndScreens";
import FloorRecapScreen from "./FloorRecapScreen";
import StartScreen from "./StartScreen";

const API = process.env.REACT_APP_API_URL;
// Nombre de cases visibles à la fois en Aventure (plateau réel : 15x15) —
// ajustable, 9 donne un bon compromis contexte/taille de case.
const ADVENTURE_VIEWPORT_TILES = 9;

const Dungeon = () => {
  const { user } = useContext(UserContext);
  const [gameData, setGameData] = useState(null);
  const [activeGames, setActiveGames] = useState(undefined); // undefined = pas encore vérifié
  const [heroPosition, setHeroPosition] = useState([0, 0]);
  const [selectedDirection, setSelectedDirection] = useState(null);
  const [tileMessage, setTileMessage] = useState(null);
  const [combatLog, setCombatLog] = useState([]);
  const [error, setError] = useState(null);
  const [isBusy, setIsBusy] = useState(false);
  const [isInventoryOpen, setIsInventoryOpen] = useState(false);
  const [isBugReportOpen, setIsBugReportOpen] = useState(false);
  const [isHeroWalking, setIsHeroWalking] = useState(false);
  const [heroFacing, setHeroFacing] = useState("bas");
  const [trapFlash, setTrapFlash] = useState(false);
  const walkingTimeoutRef = useRef(null);

  // Une fois l'animation de marche terminée (héros à l'arrêt), il revient
  // face à la caméra plutôt que de rester figé dans sa dernière direction.
  useEffect(() => {
    if (!isHeroWalking) {
      setHeroFacing("bas");
    }
  }, [isHeroWalking]);
  const [interactionDismissed, setInteractionDismissed] = useState(false);
  const [combatResultOverlay, setCombatResultOverlay] = useState(null);

  const movesRemaining = gameData?.gameState?.movesRemaining ?? 0;

  // ---------------------------------------------------------------------
  // Liste des parties en cours (n'en crée aucune automatiquement)
  // ---------------------------------------------------------------------
  const fetchMyGames = () => {
    axios
      .post(`${API}/api/dungeon/my-games`)
      .then((res) => setActiveGames(res.data.games))
      .catch((err) => {
        console.error(err);
        setActiveGames([]);
      });
  };

  useEffect(() => {
    fetchMyGames();
  }, []);

  const resumeGame = (game) => {
    setGameData(game);
    const { x, y } = game.gameState.currentTile;
    setHeroPosition([x, y]);
    setCombatResultOverlay(null);
    setInteractionDismissed(false);
  };

  const abandonFromList = (gameId) => {
    axios
      .post(`${API}/api/dungeon/abandon-game`, { gameId })
      .then(() => fetchMyGames())
      .catch((err) => console.error(err));
  };

  const startNewGame = (difficulty, mode = "normal") => {
    axios
      .post(`${API}/api/dungeon/create-game`, { difficulty, mode })
      .then((res) => {
        setGameData(res.data);
        const { x, y } = res.data.gameState.currentTile;
        setHeroPosition([x, y]);
        setTileMessage(null);
        setCombatLog([]);
        setError(null);
        setCombatResultOverlay(null);
        setInteractionDismissed(false);
      })
      .catch((err) => console.error(err));
  };

  useEffect(() => {
    if (gameData?.gameState) {
      setSelectedDirection(gameData.gameState.lockedDirection ?? null);
    }
  }, [gameData]);

  // Un popup optionnel (clé/coffre/magasin) fermé manuellement redevient
  // proposable dès que le héros change de case.
  const [heroX, heroY] = heroPosition;
  useEffect(() => {
    setInteractionDismissed(false);
  }, [heroX, heroY]);

  const heroReady =
    gameData &&
    (gameData.hero.bodyParts?.tete ?? 0) +
      (gameData.hero.bodyParts?.torse ?? 0) +
      (gameData.hero.bodyParts?.jambes ?? 0) >
      0;

  // ---------------------------------------------------------------------
  // Création du héros
  // ---------------------------------------------------------------------
  const createHero = () => {
    axios
      .post(`${API}/api/dungeon/roll-three-dices`, { gameId: gameData._id })
      .then((res) => {
        setGameData(res.data.gameData);
        return axios.post(`${API}/api/dungeon/roll-weapon-die`, {
          gameId: gameData._id,
        });
      })
      .then((res) => setGameData(res.data.gameData))
      .catch((err) => {
        console.error("Erreur createHero :", err.response?.data || err.message);
        setError(
          err.response?.data?.error || "Erreur lors de la création du héros",
        );
      });
  };

  const rerollHero = createHero; // même logique : relancer les 2 dés d'un coup

  const confirmHero = () => {
    axios
      .post(`${API}/api/dungeon/confirm-hero`, { gameId: gameData._id })
      .then((res) => setGameData(res.data.gameData))
      .catch((err) => {
        console.error(err);
        setError(
          err.response?.data?.error ||
            "Erreur lors de la confirmation du héros",
        );
      });
  };

  const chooseHeroSprite = (spriteId) => {
    axios
      .post(`${API}/api/dungeon/choose-hero-sprite`, {
        gameId: gameData._id,
        spriteId,
      })
      .then((res) => setGameData(res.data.gameData))
      .catch((err) => {
        console.error(err);
        setError(
          err.response?.data?.error || "Erreur lors du choix d'apparence",
        );
      });
  };

  // ---------------------------------------------------------------------
  // Déplacement
  // ---------------------------------------------------------------------
  const revealCurrentTile = () => {
    axios
      .post(`${API}/api/dungeon/reveal-tile`, { gameId: gameData._id })
      .then((res) => {
        setGameData(res.data.gameData);
        const { x, y } = res.data.gameData.gameState.currentTile;
        setHeroPosition([x, y]);
        setTileMessage(res.data.message);
      })
      .catch((err) => {
        console.error(err);
        setError(err.response?.data?.error || "Erreur pendant la révélation");
      });
  };

  const rollMoveDice = () => {
    if (movesRemaining > 0 || isBusy) return;
    setIsBusy(true);

    axios
      .post(`${API}/api/dungeon/roll-dice`, { gameId: gameData._id })
      .then((res) => {
        setGameData(res.data.gameData);
        setTileMessage(res.data.message || null);
        setCombatLog([]);
        setError(null);
        setInteractionDismissed(false); // nouveau tour = un popup fermé redevient proposable
      })
      .catch((err) => {
        console.error(err);
        setError(err.response?.data?.error || "Erreur lors du lancer de dés");
      })
      .finally(() => setIsBusy(false));
  };

  const moveOneStep = (direction) => {
    if (!gameData || movesRemaining <= 0 || isBusy) return;
    if (selectedDirection !== null && direction !== selectedDirection) return;

    if (walkingTimeoutRef.current) clearTimeout(walkingTimeoutRef.current);
    setIsBusy(true);
    setIsHeroWalking(true);
    setHeroFacing(direction);

    axios
      .post(`${API}/api/dungeon/move-one-step`, {
        gameId: gameData._id,
        direction,
      })
      .then((res) => {
        const {
          gameData: updatedGame,
          message,
          stopped,
          direction: actualDirection,
        } = res.data;

        setGameData(updatedGame);
        setHeroPosition([
          updatedGame.gameState.currentTile.x,
          updatedGame.gameState.currentTile.y,
        ]);
        // Le serveur renvoie toujours la direction réelle du pas (post-rebond
        // éventuel), y compris sur le tout dernier pas où lockedDirection est
        // vidé — contrairement à lui, "direction" ne ment jamais.
        if (actualDirection) setHeroFacing(actualDirection);
        setTileMessage(message);
        setError(null);

        if (
          stopped &&
          updatedGame.gameState.movesRemaining === 0 &&
          !updatedGame.gameState.pendingTrapChoice &&
          !updatedGame.gameState.pendingCombat &&
          !updatedGame.gameState.pendingEnemyChoice
        ) {
          revealCurrentTile();
        }
      })
      .catch((err) => {
        console.error(err);
        setError(err.response?.data?.error || "Erreur lors du déplacement");
      })
      .finally(() => {
        setIsBusy(false);
        walkingTimeoutRef.current = setTimeout(
          () => setIsHeroWalking(false),
          450,
        );
      });
  };

  // Déroule automatiquement tous les mouvements restants dans une direction,
  // en s'arrêtant net dès qu'une décision du joueur devient nécessaire
  // (piège, ennemi, gouffre, mort...) — même logique que le pas-à-pas,
  // simplement enchaînée en boucle côté client.
  // Petite pause entre deux pas d'un déplacement automatique : sans elle, les
  // requêtes s'enchaînent plus vite que le cycle de marche (0,45s) n'a le
  // temps de se jouer, et le héros semble juste "téléporter" d'un bout à
  // l'autre du trajet.
  const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  const runAllMoves = async (direction) => {
    if (!gameData || movesRemaining <= 0 || isBusy) return;
    if (selectedDirection !== null && direction !== selectedDirection) return;

    if (walkingTimeoutRef.current) clearTimeout(walkingTimeoutRef.current);
    setIsBusy(true);
    setIsHeroWalking(true);
    setHeroFacing(direction);
    setError(null);

    let keepGoing = true;
    let currentDirection = direction; // suit le rebond éventuel d'un pas à l'autre
    const collectedMessages = []; // rien ne se perd, même sur les pas intermédiaires

    while (keepGoing) {
      try {
        const res = await axios.post(`${API}/api/dungeon/move-one-step`, {
          gameId: gameData._id,
          direction: currentDirection,
        });
        const {
          gameData: updatedGame,
          message,
          stopped,
          direction: actualDirection,
        } = res.data;

        setGameData(updatedGame);
        setHeroPosition([
          updatedGame.gameState.currentTile.x,
          updatedGame.gameState.currentTile.y,
        ]);
        if (message) collectedMessages.push(message);

        // Le serveur renvoie toujours la direction réelle du pas (post-rebond
        // éventuel, jamais vidée contrairement à lockedDirection) — on la
        // reprend pour l'affichage ET pour orienter le pas suivant.
        if (actualDirection) {
          currentDirection = actualDirection;
          setHeroFacing(actualDirection);
        }

        const gs = updatedGame.gameState;

        const needsPlayerChoice =
          gs.pendingTrapChoice ||
          gs.pendingCombat ||
          gs.pendingEnemyChoice ||
          gs.pendingGouffreFall ||
          gs.heroIsDead;

        if (needsPlayerChoice) {
          keepGoing = false; // popup à afficher, on ne va pas plus loin sans le joueur
        } else if (stopped && gs.movesRemaining === 0) {
          keepGoing = false;
          await sleep(450); // laisse le dernier pas s'afficher avant la révélation
          const revealRes = await axios.post(`${API}/api/dungeon/reveal-tile`, {
            gameId: gameData._id,
          });
          setGameData(revealRes.data.gameData);
          setHeroPosition([
            revealRes.data.gameData.gameState.currentTile.x,
            revealRes.data.gameData.gameState.currentTile.y,
          ]);
          if (revealRes.data.message)
            collectedMessages.push(revealRes.data.message);
        } else if (gs.movesRemaining <= 0) {
          keepGoing = false;
        } else {
          await sleep(450); // laisse le temps à un cycle de marche complet de se jouer
        }
      } catch (err) {
        console.error(err);
        setError(err.response?.data?.error || "Erreur lors du déplacement");
        keepGoing = false;
      }
    }

    if (collectedMessages.length > 0) {
      setTileMessage(collectedMessages.join(" ⋅ "));
    }

    setIsBusy(false);
    setIsHeroWalking(false);
  };

  const stopMovement = () => {
    if (isBusy) return;
    setIsBusy(true);

    axios
      .post(`${API}/api/dungeon/stop-movement`, { gameId: gameData._id })
      .then((res) => {
        setGameData(res.data.gameData);
        revealCurrentTile();
      })
      .catch((err) => console.error(err))
      .finally(() => setIsBusy(false));
  };

  // ---------------------------------------------------------------------
  // Pièges permanents
  // ---------------------------------------------------------------------
  const resolveTrapChoice = (choice) => {
    if (isBusy) return;
    setIsBusy(true);

    axios
      .post(`${API}/api/dungeon/resolve-trap-choice`, {
        gameId: gameData._id,
        choice,
      })
      .then((res) => {
        setGameData(res.data.gameData);
        const { x, y } = res.data.gameData.gameState.currentTile;
        setHeroPosition([x, y]);
        setTileMessage(res.data.message);

        if (res.data.heroDied) return;
        if (res.data.stopped) revealCurrentTile();
      })
      .catch((err) => {
        console.error(err);
        setError(
          err.response?.data?.error || "Erreur lors de la résolution du piège",
        );
      })
      .finally(() => setIsBusy(false));
  };

  // ---------------------------------------------------------------------
  // Jet de sauvetage lors de la découverte d'un gouffre
  // ---------------------------------------------------------------------
  const rollGouffreFall = () => {
    if (isBusy) return;
    setIsBusy(true);

    axios
      .post(`${API}/api/dungeon/roll-gouffre-fall`, { gameId: gameData._id })
      .then((res) => {
        setGameData(res.data.gameData);
        setTileMessage(res.data.message);

        if (res.data.success) {
          const { x, y } = res.data.gameData.gameState.currentTile;
          setHeroPosition([x, y]);
        }
        // En cas d'échec, on reste affiché sur le popup (état "failed") en
        // attendant que le joueur confirme via confirmGouffreDeath.
      })
      .catch((err) => {
        console.error(err);
        setError(
          err.response?.data?.error || "Erreur lors du jet de sauvetage",
        );
      })
      .finally(() => setIsBusy(false));
  };

  const confirmGouffreDeath = () => {
    if (isBusy) return;
    setIsBusy(true);

    axios
      .post(`${API}/api/dungeon/confirm-gouffre-death`, {
        gameId: gameData._id,
      })
      .then((res) => {
        setGameData(res.data.gameData);
        setTileMessage(res.data.message);
      })
      .catch((err) => {
        console.error(err);
        setError(err.response?.data?.error || "Erreur");
      })
      .finally(() => setIsBusy(false));
  };

  // ---------------------------------------------------------------------
  // Ennemi déjà révélé (furtivité / combat / arrêt)
  // ---------------------------------------------------------------------
  const resolveEnemyChoice = (choice) => {
    if (isBusy) return;
    setIsBusy(true);

    axios
      .post(`${API}/api/dungeon/resolve-enemy-choice`, {
        gameId: gameData._id,
        choice,
      })
      .then((res) => {
        setGameData(res.data.gameData);
        const { x, y } = res.data.gameData.gameState.currentTile;
        setHeroPosition([x, y]);
        setTileMessage(res.data.message);

        if (
          res.data.stopped &&
          res.data.gameData.gameState.movesRemaining === 0 &&
          !res.data.gameData.gameState.pendingTrapChoice &&
          !res.data.gameData.gameState.pendingCombat &&
          !res.data.gameData.gameState.pendingEnemyChoice
        ) {
          revealCurrentTile();
        }
      })
      .catch((err) => {
        console.error(err);
        setError(err.response?.data?.error || "Erreur");
      })
      .finally(() => setIsBusy(false));
  };

  // ---------------------------------------------------------------------
  // Magasin / Clé / Coffre
  // ---------------------------------------------------------------------
  const buyItem = (itemKey) => {
    axios
      .post(`${API}/api/dungeon/buy-item`, { gameId: gameData._id, itemKey })
      .then((res) => {
        setGameData(res.data.gameData);
        setTileMessage(res.data.message);
      })
      .catch((err) => {
        console.error(err);
        setError(err.response?.data?.error || "Erreur lors de l'achat");
      });
  };

  const pickUpKey = () => {
    axios
      .post(`${API}/api/dungeon/pickup-key`, { gameId: gameData._id })
      .then((res) => {
        setGameData(res.data.gameData);
        setTileMessage(res.data.message);
      })
      .catch((err) => {
        console.error(err);
        setError(err.response?.data?.error || "Erreur lors du ramassage");
      });
  };

  const openChest = () => {
    axios
      .post(`${API}/api/dungeon/open-chest`, { gameId: gameData._id })
      .then((res) => {
        setGameData(res.data.gameData);
        setTileMessage(res.data.message);
      })
      .catch((err) => {
        console.error(err);
        setError(
          err.response?.data?.error || "Erreur lors de l'ouverture du coffre",
        );
      });
  };

  // ---------------------------------------------------------------------
  // Combat
  // ---------------------------------------------------------------------
  const startCombat = () => {
    axios
      .post(`${API}/api/dungeon/start-combat`, { gameId: gameData._id })
      .then((res) => {
        setGameData(res.data.gameData);
        setCombatLog([]);
      })
      .catch((err) => {
        console.error(err);
        setError(err.response?.data?.error || "Erreur pendant le combat");
      });
  };

  const declineCombat = () => {
    axios
      .post(`${API}/api/dungeon/decline-combat`, { gameId: gameData._id })
      .then((res) => setGameData(res.data.gameData))
      .catch((err) => {
        console.error(err);
        setError(err.response?.data?.error || "Erreur");
      });
  };

  const attemptHideForced = () => {
    if (isBusy) return;
    setIsBusy(true);

    axios
      .post(`${API}/api/dungeon/attempt-hide-forced`, { gameId: gameData._id })
      .then((res) => {
        setGameData(res.data.gameData);
        setTileMessage(res.data.message);
      })
      .catch((err) => {
        console.error(err);
        setError(err.response?.data?.error || "Erreur");
      })
      .finally(() => setIsBusy(false));
  };

  const attackRound = () => {
    if (isBusy) return;
    setIsBusy(true);

    axios
      .post(`${API}/api/dungeon/attack-round`, { gameId: gameData._id })
      .then((res) => {
        setGameData(res.data.gameData);
        setCombatLog((prev) => [...prev, res.data.log]); // un round = un bloc de lignes (ordre interne préservé)

        if (res.data.victory) {
          setCombatResultOverlay({
            type: "victory",
            log: res.data.log,
            goldReward: res.data.goldReward,
          });
        } else if (res.data.gameData.gameState.heroIsDead) {
          setCombatResultOverlay({ type: "defeat", log: res.data.log });
        }
      })
      .catch((err) => {
        console.error(err);
        setError(err.response?.data?.error || "Erreur pendant le combat");
      })
      .finally(() => setIsBusy(false));
  };

  const dismissCombatResult = () => setCombatResultOverlay(null);

  const stopCombat = () => {
    axios
      .post(`${API}/api/dungeon/stop-combat`, { gameId: gameData._id })
      .then((res) => {
        setGameData(res.data.gameData);
        const { x, y } = res.data.gameData.gameState.currentTile;
        setHeroPosition([x, y]);
        setTileMessage(res.data.message);
        setCombatLog([]);

        if (!res.data.heroDied) revealCurrentTile();
      })
      .catch((err) => {
        console.error(err);
        setError(err.response?.data?.error || "Erreur pendant le combat");
      });
  };

  // ---------------------------------------------------------------------
  // Mort / abandon / nouvelle partie
  // ---------------------------------------------------------------------
  const recreateHero = () => {
    axios
      .post(`${API}/api/dungeon/recreate-hero`, { gameId: gameData._id })
      .then((res) => {
        setGameData(res.data.gameData);
        const { x, y } = res.data.gameData.gameState.currentTile;
        setHeroPosition([x, y]);
        setTileMessage("Un nouveau héros se dresse à l'entrée du donjon.");
      })
      .catch((err) => {
        console.error(err);
        setError(
          err.response?.data?.error || "Erreur lors de la recréation du héros",
        );
      });
  };

  const abandonGame = () => {
    axios
      .post(`${API}/api/dungeon/abandon-game`, { gameId: gameData._id })
      .then((res) => setGameData(res.data.gameData))
      .catch((err) => console.error(err));
  };

  // Retour à l'écran de démarrage (choix reprendre / nouvelle partie) après
  // une victoire ou un abandon, plutôt que de recréer directement une partie.
  const returnToStartScreen = () => {
    setGameData(null);
    setTileMessage(null);
    setCombatLog([]);
    setError(null);
    setCombatResultOverlay(null);
    setInteractionDismissed(false);
    fetchMyGames();
  };

  const dismissFloorRecap = () => {
    axios
      .post(`${API}/api/dungeon/dismiss-floor-recap`, { gameId: gameData._id })
      .then((res) => {
        setGameData(res.data.gameData);
        const { x, y } = res.data.gameData.gameState.currentTile;
        setHeroPosition([x, y]);
      })
      .catch((err) => console.error(err));
  };

  const continueToNextFloor = () => {
    dismissFloorRecap();
  };

  const saveAndQuitFromRecap = () => {
    axios
      .post(`${API}/api/dungeon/dismiss-floor-recap`, { gameId: gameData._id })
      .then(() => returnToStartScreen())
      .catch((err) => console.error(err));
  };

  // ---------------------------------------------------------------------
  // Utilisation des objets
  // ---------------------------------------------------------------------
  const useItem = (itemKey, extra = {}) => {
    axios
      .post(`${API}/api/dungeon/use-item`, {
        gameId: gameData._id,
        itemKey,
        ...extra,
      })
      .then((res) => {
        setGameData(res.data.gameData);
        setTileMessage(res.data.message);
      })
      .catch((err) => {
        console.error(err);
        setError(
          err.response?.data?.error ||
            "Erreur lors de l'utilisation de l'objet",
        );
      });
  };

  const usePotion = (itemKey, bodyPart) => useItem(itemKey, { bodyPart });
  const useBombeLigne = (itemKey, direction) => useItem(itemKey, { direction });

  const useRadar = () => {
    if (isBusy) return;
    setIsBusy(true);
    axios
      .post(`${API}/api/dungeon/use-radar-pieges`, { gameId: gameData._id })
      .then((res) => {
        setGameData(res.data.gameData);
        setTileMessage(res.data.message);
        // Flash purement visuel côté front — aucune tuile n'est marquée
        // "revealed" côté serveur, l'effet s'efface tout seul après 1,5s.
        setTrapFlash(true);
        setTimeout(() => setTrapFlash(false), 1500);
      })
      .catch((err) => {
        console.error(err);
        setError(
          err.response?.data?.error ||
            "Erreur lors de l'utilisation de la capacité",
        );
      })
      .finally(() => setIsBusy(false));
  };

  // ---------------------------------------------------------------------
  // Helpers d'affichage
  // ---------------------------------------------------------------------
  const isHeroOnShop = () => {
    if (!gameData?.gameState?.currentTile) return false;
    if (movesRemaining > 0) return false; // pas juste en passant, il faut finir son tour dessus
    const { x, y } = gameData.gameState.currentTile;
    const tile = gameData.tiles.find(
      (t) => t.position.x === x && t.position.y === y,
    );
    return tile?.type === "magasin" && tile.revealed;
  };

  const isHeroOnKey = () => {
    if (!gameData?.gameState?.currentTile) return false;
    const { x, y } = gameData.gameState.currentTile;
    const tile = gameData.tiles.find(
      (t) => t.position.x === x && t.position.y === y,
    );
    return tile?.type === "clé" && tile.revealed && !tile.cleared;
  };

  const isHeroOnChest = () => {
    if (!gameData?.gameState?.currentTile) return false;
    const { x, y } = gameData.gameState.currentTile;
    const tile = gameData.tiles.find(
      (t) => t.position.x === x && t.position.y === y,
    );
    return tile?.type === "coffre" && tile.revealed && !tile.cleared;
  };

  // ---------------------------------------------------------------------
  // Rendu
  // ---------------------------------------------------------------------
  if (activeGames === undefined) return <div>Chargement...</div>;

  if (!gameData) {
    return (
      <StartScreen
        activeGames={activeGames}
        onResume={resumeGame}
        onAbandon={abandonFromList}
        onStartNew={startNewGame}
      />
    );
  }

  if (!gameData.tiles) return <div>Chargement...</div>;

  if (gameData.status === "victory")
    return <VictoryScreen onNewGame={returnToStartScreen} />;
  if (gameData.status === "abandoned")
    return <GameOverScreen onNewGame={returnToStartScreen} />;
  if (gameData.status === "defeat") {
    return (
      <DefeatScreen
        score={gameData.gameState.score}
        floor={gameData.gameState.floor}
        deathCause={gameData.gameState.deathCause}
        onNewGame={returnToStartScreen}
      />
    );
  }
  if (gameData.gameState.floorRecap) {
    return (
      <FloorRecapScreen
        recap={gameData.gameState.floorRecap}
        onContinue={continueToNextFloor}
        onSaveAndQuit={saveAndQuitFromRecap}
      />
    );
  }

  const pendingTrap = gameData.gameState.pendingTrapChoice;
  const pendingCombat = gameData.gameState.pendingCombat;
  const radarCost = (gameData.gameState.radarUsedCount || 0) * 5;

  // En Aventure, le plateau n'est pas un rectangle plein — on grise les
  // flèches qui mèneraient vers une case inexistante (mur/pas de salle),
  // plutôt que de laisser cliquer pour se le faire refuser par le serveur.
  const ADVENTURE_ROOM_SIZE = 3;
  const canCrossToRoomFront = (fromX, fromY, toX, toY) => {
    const sameRoom =
      Math.floor(fromX / ADVENTURE_ROOM_SIZE) ===
        Math.floor(toX / ADVENTURE_ROOM_SIZE) &&
      Math.floor(fromY / ADVENTURE_ROOM_SIZE) ===
        Math.floor(toY / ADVENTURE_ROOM_SIZE);
    if (sameRoom) return true;
    if (fromX !== toX)
      return (
        fromY % ADVENTURE_ROOM_SIZE === 1 && toY % ADVENTURE_ROOM_SIZE === 1
      );
    return fromX % ADVENTURE_ROOM_SIZE === 1 && toX % ADVENTURE_ROOM_SIZE === 1;
  };

  const blockedDirections =
    gameData.mode === "aventure"
      ? ["haut", "bas", "gauche", "droite"].filter((dir) => {
          const { dx, dy } = {
            haut: { dx: 0, dy: -1 },
            bas: { dx: 0, dy: 1 },
            gauche: { dx: -1, dy: 0 },
            droite: { dx: 1, dy: 0 },
          }[dir];
          const { x, y } = gameData.gameState.currentTile;
          const tx = x + dx;
          const ty = y + dy;
          const tileExists = gameData.tiles.some(
            (t) => t.position.x === tx && t.position.y === ty,
          );
          return !tileExists || !canCrossToRoomFront(x, y, tx, ty);
        })
      : [];
  // Stats affichées dans les panneaux de rencontre (1ère fois ou déjà
  // révélé) — lues directement depuis les données déjà connues côté front
  // (game.boss pour le boss, la tuile elle-même sinon), sans appel serveur.
  const getEnemyStatsForDisplay = (enemyType, x, y) => {
    if (enemyType === "boss") {
      return {
        bodyParts: gameData.boss.bodyParts,
        weaponDie: gameData.boss.weaponDie,
      };
    }
    const tile = gameData.tiles.find(
      (t) => t.position.x === x && t.position.y === y,
    );
    if (!tile) return null;
    return tile.mergedStats
      ? {
          bodyParts: tile.mergedStats.bodyParts,
          weaponDie: tile.mergedStats.weaponDie,
        }
      : { pv: tile.value, weaponDie: tile.weaponDie };
  };

  const pendingEnemyChoice = gameData.gameState.pendingEnemyChoice;
  const pendingGouffreFall = gameData.gameState.pendingGouffreFall;
  const heroIsDead = gameData.gameState.heroIsDead;
  const heroConfirmed = gameData.gameState.heroConfirmed;

  // Détermine LE panneau prioritaire à afficher en superposition du plateau
  // (mort, combat, piège, ennemi, clé, coffre, magasin) — un seul à la fois.
  // "dismissable" : peut être fermé sans agir (clé/coffre/magasin uniquement —
  // combat/piège/ennemi ont déjà leurs propres boutons de sortie intégrés).
  const getPendingAction = () => {
    if (combatResultOverlay) {
      return {
        dismissable: false,
        content: (
          <CombatResultPanel
            result={combatResultOverlay}
            onContinue={dismissCombatResult}
          />
        ),
      };
    }
    if (heroIsDead) {
      return {
        dismissable: false,
        content: (
          <DeathPanel
            livesRemaining={gameData.gameState.livesRemaining}
            onRecreateHero={recreateHero}
            onAbandonGame={abandonGame}
          />
        ),
      };
    }
    if (!heroReady || !heroConfirmed) {
      return {
        dismissable: false,
        content: (
          <HeroPanel
            hero={gameData.hero}
            heroReady={heroReady}
            heroConfirmed={heroConfirmed}
            gameState={gameData.gameState}
            onCreateHero={createHero}
            onRerollHero={rerollHero}
            onConfirmHero={confirmHero}
            onChooseSprite={chooseHeroSprite}
          />
        ),
      };
    }
    if (pendingGouffreFall) {
      return {
        dismissable: false,
        content: (
          <GouffreFallPanel
            pendingGouffreFall={pendingGouffreFall}
            isBusy={isBusy}
            onRoll={rollGouffreFall}
            onConfirmDeath={confirmGouffreDeath}
          />
        ),
      };
    }
    if (pendingCombat && !pendingCombat.started) {
      return {
        dismissable: false,
        content: (
          <CombatChoicePanel
            enemyType={pendingCombat.enemyType}
            forced={pendingCombat.forced}
            stats={getEnemyStatsForDisplay(
              pendingCombat.enemyType,
              pendingCombat.x,
              pendingCombat.y,
            )}
            onStartCombat={startCombat}
            onDeclineCombat={declineCombat}
            onAttemptHide={attemptHideForced}
          />
        ),
      };
    }
    if (pendingCombat?.started) {
      return {
        dismissable: false,
        content: (
          <CombatPanel
            pendingCombat={pendingCombat}
            hero={gameData.hero}
            combatLog={combatLog}
            isBusy={isBusy}
            onAttack={attackRound}
            onStopCombat={stopCombat}
          />
        ),
      };
    }
    if (pendingTrap) {
      return {
        dismissable: false,
        content: (
          <TrapChoicePanel
            pendingTrap={pendingTrap}
            tiles={gameData.tiles}
            isBusy={isBusy}
            onResolve={resolveTrapChoice}
          />
        ),
      };
    }
    if (pendingEnemyChoice) {
      return {
        dismissable: false,
        content: (
          <EnemyChoicePanel
            pendingEnemyChoice={pendingEnemyChoice}
            isBusy={isBusy}
            stats={getEnemyStatsForDisplay(
              pendingEnemyChoice.enemyType,
              pendingEnemyChoice.enemyX,
              pendingEnemyChoice.enemyY,
            )}
            onResolve={resolveEnemyChoice}
          />
        ),
      };
    }
    if (interactionDismissed) return null;

    if (isHeroOnKey()) {
      return {
        dismissable: true,
        content: <KeyPanel onPickUpKey={pickUpKey} />,
      };
    }
    if (isHeroOnChest()) {
      return {
        dismissable: true,
        content: <ChestPanel onOpenChest={openChest} />,
      };
    }
    if (isHeroOnShop()) {
      return {
        dismissable: true,
        content: (
          <ShopPanel shopStock={gameData.shopStock} onBuyItem={buyItem} />
        ),
      };
    }
    return null;
  };

  const pendingAction = getPendingAction();

  return (
    <div className="dungeon-board">
      <div className="dungeon-header">
        <h1 className="game-title">Skip the Dungeon</h1>
        <div className="dungeon-header-meta">
          <span>Étage {gameData.gameState.floor}</span>
          <span className="hero-stats-sep">·</span>
          <span>{gameData.difficulty}</span>
          {gameData.mode === "aventure" && (
            <>
              <span className="hero-stats-sep">·</span>
              <span>Aventure</span>
            </>
          )}
          <span className="hero-stats-sep">·</span>
          <span className="dungeon-username">{user?.username}</span>
        </div>
        <div className="dungeon-header-actions">
          <button
            onClick={returnToStartScreen}
            className="save-and-quit-button"
          >
            💾 Sauvegarder et quitter
          </button>
          <button
            onClick={() => setIsBugReportOpen(true)}
            className="bug-report-button"
          >
            🐛 Signaler un bug
          </button>
        </div>
      </div>

      {heroReady && heroConfirmed && (
        <HeroStatsBar hero={gameData.hero} gameState={gameData.gameState} />
      )}

      <div className="board-wrapper">
        {gameData.mode === "aventure" ? (
          <div className="board-viewport">
            <div
              className="board-viewport-inner"
              style={{
                width: `${(15 / ADVENTURE_VIEWPORT_TILES) * 100}%`,
                height: `${(15 / ADVENTURE_VIEWPORT_TILES) * 100}%`,
                transform: `translate(${-((heroPosition[0] + 0.5 - ADVENTURE_VIEWPORT_TILES / 2) / 15) * 100}%, ${-((heroPosition[1] + 0.5 - ADVENTURE_VIEWPORT_TILES / 2) / 15) * 100}%)`,
              }}
            >
              <DungeonGrid
                tiles={gameData.tiles}
                heroPosition={heroPosition}
                heroIsDead={heroIsDead}
                groundLoot={gameData.gameState.groundLoot}
                exitReady={
                  gameData.gameState.keyFound && gameData.gameState.bossDefeated
                }
                heroSpriteId={gameData.hero.spriteId}
                heroFacing={heroFacing}
                heroIsWalking={isHeroWalking}
                solVariant={gameData.gameState.solVariant}
                trapFlash={trapFlash}
                gridSize={15}
                isAventure={true}
                fillParent={true}
              />
            </div>
            <Minimap tiles={gameData.tiles} heroPosition={heroPosition} />
          </div>
        ) : (
          <DungeonGrid
            tiles={gameData.tiles}
            heroPosition={heroPosition}
            heroIsDead={heroIsDead}
            groundLoot={gameData.gameState.groundLoot}
            exitReady={
              gameData.gameState.keyFound && gameData.gameState.bossDefeated
            }
            heroSpriteId={gameData.hero.spriteId}
            heroFacing={heroFacing}
            heroIsWalking={isHeroWalking}
            solVariant={gameData.gameState.solVariant}
            trapFlash={trapFlash}
            gridSize={8}
            isAventure={false}
          />
        )}

        {isBugReportOpen ? (
          <ActionOverlay onClose={() => setIsBugReportOpen(false)}>
            <BugReportForm onClose={() => setIsBugReportOpen(false)} />
          </ActionOverlay>
        ) : isInventoryOpen ? (
          <ActionOverlay onClose={() => setIsInventoryOpen(false)}>
            <InventoryPanel
              inventory={gameData.hero.inventory}
              isBusy={isBusy}
              inCombat={!!pendingCombat?.started}
              alreadyUsedThisTurn={
                gameData.gameState.lastItemUseTurn ===
                gameData.gameState.turnCount
              }
              onUsePotion={usePotion}
              onUsePotionTriple={useItem}
              onUseWeapon={useItem}
              onUseBombeCarre={useItem}
              onUseBombeLigne={useBombeLigne}
            />
          </ActionOverlay>
        ) : (
          pendingAction && (
            <ActionOverlay
              onClose={
                pendingAction.dismissable
                  ? () => setInteractionDismissed(true)
                  : undefined
              }
            >
              {pendingAction.content}
            </ActionOverlay>
          )
        )}
      </div>

      <div className="board-controls">
        {heroReady && heroConfirmed && !heroIsDead && !pendingAction && (
          <MovementPanel
            movesRemaining={movesRemaining}
            selectedDirection={selectedDirection}
            isBusy={isBusy}
            blockedDirections={blockedDirections}
            onRollDice={rollMoveDice}
            onMoveOneStep={moveOneStep}
            onRunAllMoves={runAllMoves}
            onStopMovement={stopMovement}
          />
        )}

        {tileMessage && <p className="tile-message">{tileMessage}</p>}
        {error && <p className="dungeon-error-message">{error}</p>}

        {heroReady && heroConfirmed && (
          <div className="ability-buttons-row">
            <button
              className="inventory-toggle-button"
              onClick={() => setIsInventoryOpen(true)}
            >
              <img src={inventoryImage} alt="" className="backpack-icon" />{" "}
              Inventaire
            </button>
            <button
              className="radar-button"
              onClick={useRadar}
              disabled={
                isBusy ||
                pendingCombat?.started ||
                (gameData.gameState.score || 0) < radarCost
              }
              title={
                radarCost > 0
                  ? `Révèle temporairement les pièges de l'étage (-${radarCost} points)`
                  : "Révèle temporairement les pièges de l'étage (gratuit la 1ère fois)"
              }
            >
              📡 Détecter les pièges (
              {radarCost > 0 ? `-${radarCost} pts` : "gratuit"})
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Dungeon;
